import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, Sparkles } from 'lucide-react';

/**
 * Reusable FAQ Section Component
 * 
 * @param {Object} props
 * @param {string} props.badgeText - E.g. "Ofte stillede spørgsmål"
 * @param {string} props.title - E.g. "Spørgsmål & svar om skruefundering"
 * @param {string} props.description - E.g. "Få svar på de mest almindelige spørgsmål..."
 * @param {Array<{question: string, answer: string}>} props.items - List of FAQs
 * @param {string} [props.themeColor] - "amber" | "orange"
 * @param {string} [props.bgClass] - Background class override
 */
export default function FAQSection({
  badgeText = "Ofte stillede spørgsmål",
  title = "Spørgsmål & svar",
  description = "Få svar på de mest almindelige spørgsmål om produkter, montering og bæreevne.",
  items = [],
  themeColor = "amber",
  bgClass = "bg-white",
  id = "faq"
}) {
  // Allow opening/closing individual items; default first item open for instant clarity
  const [openIndex, setOpenIndex] = useState(0);

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const isAmber = themeColor === 'amber';
  const badgeClasses = isAmber 
    ? "bg-amber-500/10 text-amber-700 border-amber-500/20" 
    : "bg-orange-100 text-orange-700 border-orange-200";
  const activeBorderClasses = isAmber
    ? "border-amber-500/60 ring-2 ring-amber-500/10"
    : "border-orange-500/60 ring-2 ring-orange-500/10";

  return (
    <section id={id} className={`py-16 sm:py-24 ${bgClass} border-t border-slate-200 transition-colors`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs sm:text-sm font-bold uppercase tracking-widest mb-4 ${badgeClasses}`}>
            <Sparkles className="w-4 h-4" />
            <span>{badgeText}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight text-slate-900 leading-tight">
            {title}
          </h2>
          {description && (
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-4">
          {items.map((item, index) => {
            const isOpen = openIndex === index;
            const itemId = `faq-item-${index}`;
            const buttonId = `faq-button-${index}`;

            return (
              <div
                key={index}
                className={`bg-slate-50 rounded-2xl border transition-all duration-200 overflow-hidden shadow-xs ${
                  isOpen 
                    ? `bg-white ${activeBorderClasses} shadow-md` 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/80'
                }`}
              >
                <button
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={itemId}
                  onClick={() => toggleItem(index)}
                  className="w-full py-5 px-6 sm:px-7 text-left flex items-center justify-between gap-4 group cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-500"
                >
                  <div className="flex items-center gap-3.5 sm:gap-4 text-left">
                    <span className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-amber-600 transition-colors leading-snug">
                      {item.question}
                    </span>
                  </div>
                  <div
                    className={`w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center shrink-0 text-slate-500 group-hover:text-slate-900 group-hover:border-slate-300 transition-transform duration-200 bg-white ${
                      isOpen ? 'rotate-180 bg-slate-100 text-slate-900' : ''
                    }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={itemId}
                      role="region"
                      aria-labelledby={buttonId}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                    >
                      <div className="px-6 sm:px-7 pb-6 pt-1 text-slate-700 text-sm sm:text-base leading-relaxed border-t border-slate-100 mt-1">
                        <p className="whitespace-pre-line">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Small Bottom Info */}
        <div className="mt-10 p-5 rounded-xl bg-slate-100/80 border border-slate-200/80 text-center flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-left">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
              <HelpCircle className="w-5 h-5" />
            </div>
            <p className="text-xs sm:text-sm text-slate-600">
              Har du et specifikt spørgsmål til dit byggeprojekt, som ikke er nævnt her?
            </p>
          </div>
          <a
            href="#kontakt"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm font-bold shrink-0 transition-colors"
          >
            Spørg os direkte
          </a>
        </div>
      </div>
    </section>
  );
}
